import { ConversationAttachmentDTO, ConversationMessageDTO, CursorPageDTO } from 'models/conversations'
import { MouseEvent, useEffect, useRef, useState } from 'react'

import { logger } from 'utils/logging'

export const ATTACHMENT_URL_MAX_AGE_MS = 50 * 60_000

export const ATTACHMENT_URL_REFRESH_INTERVAL_MS = 60_000

export const ATTACHMENT_URL_REFRESH_MAX_PAGES = 20

export type AttachmentUrlRefreshReason = 'image_error' | 'window_focus' | 'expiry_timer' | 'file_click'

const oldestMessageOf = (messages: ConversationMessageDTO[]): ConversationMessageDTO => messages.reduce((oldest, message) =>
  new Date(message.createdAt).getTime() < new Date(oldest.createdAt).getTime() ? message : oldest)

export const fetchMessagesBackTo = async (
  fetchPage: (beforeMessageId?: string) => Promise<CursorPageDTO<ConversationMessageDTO>>,
  messageId: string
): Promise<ConversationMessageDTO[]> => {
  const fetched: ConversationMessageDTO[] = []
  let cursor: string | undefined
  for (let pages = 0; pages < ATTACHMENT_URL_REFRESH_MAX_PAGES; pages += 1) {
    const page = await fetchPage(cursor)
    fetched.push(...page.items)
    if (!page.hasMore || !page.items.length || page.items.some((message) => message.id === messageId)) break
    cursor = oldestMessageOf(page.items).id
  }
  return fetched
}

export const withFreshAttachmentUrls = (freshMessages: ConversationMessageDTO[]) => {
  const fresh = new Map(freshMessages.flatMap((message) => message.attachments ?? []).map((attachment) => [attachment.id, attachment]))
  return (message: ConversationMessageDTO): ConversationMessageDTO => {
    if (!message.attachments?.some((attachment) => fresh.has(attachment.id) && fresh.get(attachment.id)?.url !== attachment.url)) return message
    return {
      ...message,
      attachments: message.attachments.map((attachment) => {
        const update = fresh.get(attachment.id)
        return update ? { ...attachment, url: update.url } : attachment
      }),
    }
  }
}

interface Options {
  conversationId: string
  messages: ConversationMessageDTO[]
  refetch: () => Promise<ConversationMessageDTO[]>
}

const attachmentUrlsOf = (messages: ConversationMessageDTO[]): string[] => messages
  .flatMap((message) => message.attachments ?? [])
  .map((attachment) => attachment.url)
  .filter((url): url is string => !!url)

const freshUrlOf = (messages: ConversationMessageDTO[] | null, attachmentId: string): string | null => messages
  ?.flatMap((message) => message.attachments ?? [])
  .find((attachment) => attachment.id === attachmentId)?.url ?? null

const oldestOf = (seenAt: Map<string, number>): number | null => {
  const times = Array.from(seenAt.values())
  return times.length ? Math.min(...times) : null
}

const isStale = (seenAt: number | null | undefined) =>
  seenAt !== null && seenAt !== undefined && Date.now() - seenAt > ATTACHMENT_URL_MAX_AGE_MS

const logContext = (metadata: Record<string, unknown>) => ({
  context: { component: 'ConversationThread', action: 'refreshAttachmentUrls', metadata },
})

export const useAttachmentUrlRefresh = ({ conversationId, messages, refetch }: Options) => {
  const seenAtRef = useRef(new Map<string, number>())
  const lastRefreshAtRef = useRef<number | null>(null)
  const urlsBeforeRefreshRef = useRef<ReadonlySet<string> | null>(null)
  const inFlightRef = useRef<Promise<ConversationMessageDTO[] | null> | null>(null)
  const reportedRepeatFailuresRef = useRef(new Set<string>())
  const lastRefreshFailedRef = useRef(false)
  const [refreshCount, setRefreshCount] = useState(0)
  const latestRef = useRef({ conversationId, refetch })
  latestRef.current = { conversationId, refetch }

  useEffect(() => {
    lastRefreshAtRef.current = null
    urlsBeforeRefreshRef.current = null
    lastRefreshFailedRef.current = false
  }, [conversationId])

  useEffect(() => {
    const seenAt = seenAtRef.current
    const current = new Set(attachmentUrlsOf(messages))
    const now = Date.now()
    current.forEach((url) => {
      if (!seenAt.has(url)) seenAt.set(url, now)
    })
    Array.from(seenAt.keys()).forEach((url) => {
      if (!current.has(url)) seenAt.delete(url)
    })
  }, [messages])

  const refresh = (reason: AttachmentUrlRefreshReason, attachment?: ConversationAttachmentDTO): Promise<ConversationMessageDTO[] | null> => {
    if (inFlightRef.current) return inFlightRef.current
    const lastRefreshAt = lastRefreshAtRef.current
    if (lastRefreshAt !== null && Date.now() - lastRefreshAt < ATTACHMENT_URL_REFRESH_INTERVAL_MS) return Promise.resolve(null)

    const { conversationId: refreshedConversationId, refetch: run } = latestRef.current
    lastRefreshAtRef.current = Date.now()
    urlsBeforeRefreshRef.current = new Set(seenAtRef.current.keys())
    logger.info('ChatWidget: refreshing attachment URLs', undefined, logContext({
      conversationId: refreshedConversationId,
      reason,
      attachmentId: attachment?.id,
      contentType: attachment?.contentType,
    }))

    const pending = run()
      .then((fresh) => {
        lastRefreshFailedRef.current = false
        return fresh
      })
      .catch((error: unknown) => {
        if (!lastRefreshFailedRef.current) {
          logger.warn('ChatWidget: attachment URL refresh failed', error, logContext({ conversationId: refreshedConversationId, reason }))
        }
        lastRefreshFailedRef.current = true
        return null
      })
      .finally(() => {
        inFlightRef.current = null
        setRefreshCount((count) => count + 1)
      })
    inFlightRef.current = pending
    return pending
  }

  const refreshRef = useRef(refresh)
  refreshRef.current = refresh

  useEffect(() => {
    const oldest = oldestOf(seenAtRef.current)
    if (oldest === null) return
    const dueAt = Math.max(oldest + ATTACHMENT_URL_MAX_AGE_MS, (lastRefreshAtRef.current ?? 0) + ATTACHMENT_URL_REFRESH_INTERVAL_MS)
    const timer = window.setTimeout(() => void refreshRef.current('expiry_timer'), Math.max(dueAt - Date.now(), 0))
    return () => window.clearTimeout(timer)
  }, [messages, refreshCount])

  useEffect(() => {
    const seenAt = seenAtRef.current
    const onFocus = () => {
      if (isStale(oldestOf(seenAt))) void refreshRef.current('window_focus')
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [])

  const onImageError = (attachment: ConversationAttachmentDTO) => {
    const urlsBeforeRefresh = urlsBeforeRefreshRef.current
    if (urlsBeforeRefresh && attachment.url && seenAtRef.current.has(attachment.url) && !urlsBeforeRefresh.has(attachment.url)) {
      if (reportedRepeatFailuresRef.current.has(attachment.id)) return
      reportedRepeatFailuresRef.current.add(attachment.id)
      logger.warn('ChatWidget: attachment image still fails after its URL was refreshed', undefined, logContext({
        conversationId,
        attachmentId: attachment.id,
        contentType: attachment.contentType,
      }))
      return
    }
    void refresh('image_error', attachment)
  }

  const onOpenFile = (attachment: ConversationAttachmentDTO, event: MouseEvent<HTMLElement>) => {
    if (!attachment.url || !isStale(seenAtRef.current.get(attachment.url))) return
    event.preventDefault()
    const tab = window.open('', '_blank')
    if (tab) tab.opener = null
    void refresh('file_click', attachment).then((fresh) => {
      if (!tab) return
      const url = freshUrlOf(fresh, attachment.id)
      if (url) tab.location.href = url
      else tab.close()
    })
  }

  return { onImageError, onOpenFile }
}
